import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Circle, RefreshCw } from 'lucide-react';

const roadmapData = [
  {
    quarter: 'Q3 2025',
    status: 'completed',
    items: [
      { label: 'Purchase URL/Hosting', status: 'done' },
      { label: 'X (Twitter) Account Creation', status: 'done' },
      { label: 'Discord Server Setup', status: 'done' },
      { label: 'Wallet Creation (Treasury, Giveaway, Marketing, Liquidity)', status: 'done' },
      { label: 'Website Development & Launch', status: 'done' },
      { label: 'Initial Marketing Campaigns', status: 'done' },
    ],
  },
  {
    quarter: 'Q4 2025',
    status: 'completed',
    items: [
      { label: 'Token Creation & Deployment on BNB Chain', status: 'done' },
      { label: 'DEXScreener Listing', status: 'done' },
      { label: 'Game Development', status: 'done' },
      { label: 'Game Launch', status: 'done' },
      { label: 'Full Game Launch with Competitive Mode', status: 'done' },
    ],
  },
  {
    quarter: 'Q1 2026',
    status: 'active',
    items: [
      { label: 'Website Polish & Optimization', status: 'ongoing', note: 'Always working on it' },
      { label: 'Liquidity Bonding', status: 'inprogress', progress: 45 },
      { label: 'Community Giveaway Events', status: 'upcoming' },
      { label: 'Tournament System Implementation', status: 'upcoming' },
      { label: 'Prize Pools & Tournaments', status: 'upcoming' },
      { label: 'Mobile App (Portrait Mode) Optimization', status: 'upcoming' },
      { label: 'Leaderboard Rewards Distribution', status: 'upcoming' },
      { label: 'Partnership Announcements', status: 'upcoming' },
      { label: 'Community-Driven Features', status: 'upcoming' },
    ],
  },
];

const RoadmapSection = () => {
  const getQuarterBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/40';
      case 'active':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40';
      default:
        return 'bg-purple-500/20 text-purple-400 border-purple-500/40';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'inprogress':
        return <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />;
      case 'ongoing':
        return <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" style={{ animationDuration: '3s' }} />;
      default:
        return <Circle className="w-5 h-5 text-purple-400" />;
    }
  };

  return (
    <section
      id="roadmap"
      className="min-h-screen bg-gradient-to-b from-[#190019] via-[#370c2e] to-[#b33377] text-white flex flex-col items-center justify-center py-24 px-4"
    >
      <div className="w-full max-w-6xl">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-6xl font-bold text-center font-rubik drop-shadow-xl mb-4"
        >
          Roadmap
        </motion.h2>
        <p className="text-center text-white/70 mb-8 text-sm md:text-base">
          Our journey from concept to community-driven gaming platform
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {roadmapData.map((quarter, qIndex) => (
            <motion.div
              key={qIndex}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: qIndex * 0.15 }}
              className="relative"
            >
              {/* Card */}
              <div
                className={`relative overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
                  quarter.status === 'active'
                    ? 'border-yellow-500/50 shadow-yellow-500/20'
                    : 'border-white/10'
                }`}
              >
                {/* Glow effect for active quarter */}
                {quarter.status === 'active' && (
                  <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-pink-500/20 blur-xl opacity-75 -z-10" />
                )}

                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold font-rubik">{quarter.quarter}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wider ${getQuarterBadge(
                        quarter.status
                      )}`}
                    >
                      {quarter.status === 'completed' ? '✓ Done' : quarter.status === 'active' ? '◉ Active' : '○ Upcoming'}
                    </span>
                  </div>
                  {quarter.status === 'active' && (
                    <div className="flex items-center gap-2 text-xs text-yellow-300">
                      <span className="h-2 w-2 rounded-full bg-yellow-400 animate-pulse" />
                      Currently in progress
                    </div>
                  )}
                </div>

                {/* Items */}
                <div className="p-6 space-y-4">
                  {quarter.items.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: qIndex * 0.15 + index * 0.05 }}
                      className="group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex-shrink-0">{getStatusIcon(item.status)}</div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm md:text-base font-medium leading-tight ${
                              item.status === 'done'
                                ? 'text-green-300/90'
                                : item.status === 'inprogress'
                                ? 'text-yellow-300/90'
                                : item.status === 'ongoing'
                                ? 'text-blue-300/90'
                                : 'text-white/70'
                            }`}
                          >
                            {item.label}
                          </p>
                          {/* Note for ongoing items */}
                          {item.status === 'ongoing' && item.note && (
                            <p className="text-xs text-blue-400/80 mt-1 italic">{item.note}</p>
                          )}
                          {/* Progress bar for in-progress items */}
                          {item.status === 'inprogress' && item.progress && (
                            <div className="mt-2">
                              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${item.progress}%` }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 1, delay: 0.5 }}
                                  className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                                />
                              </div>
                              <p className="text-xs text-yellow-400/80 mt-1">{item.progress}% complete</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Legal Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 max-w-4xl mx-auto"
        >
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 md:p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <div className="h-2 w-2 rounded-full bg-white/40" />
              </div>
              <div className="text-xs md:text-sm text-white/60 leading-relaxed">
                <strong className="text-white/80 font-semibold">Disclaimer:</strong> This roadmap represents our current plans and goals for the Shut The Box project.
                It is provided for informational purposes only and does not constitute a binding commitment, guarantee, or promise of future performance or delivery.
                All timelines, features, and milestones are subject to change without notice based on technical, market, or operational considerations.
                By participating in this project, you acknowledge that development priorities may shift and that there is no guarantee that any specific feature or timeline will be met as outlined.
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RoadmapSection;
